import React, { useEffect, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import PercentIcon from '@mui/icons-material/Percent';
import Slider from '@mui/material/Slider';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { RootState } from '../Redux/store';
import RecurringDepositPDFGenerator from '../pdf/pdf-recurring-deposit';
import ReactGA from 'react-ga4'
import SaveTitleDialog from "../dialog-box/SaveTitleDialog";
import { saveRecurringDeposit, updateRecurringDeposit } from '../Database/recurring-deposit';
import { RecurringDeposit } from '../models/recurring-deposit';
import { IconButton, Typography } from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCurrencySymbol } from "../Redux/currencySlice";
import BootstrapTooltip from '../components/tooltip';
import SavedSnackbar from '../utils/saved-snackbar';
ChartJS.register(ArcElement, Tooltip, Legend);


const RecurringDepositCalculator: React.FC<{ recurringDeposit?: RecurringDeposit }> = ({ }) => {

  const [recurringData, setRecurringDeposit] = useState<RecurringDeposit | null>(null);
  const location = useLocation();
  const [depositAmount, setDepositAmount] = useState('5000');
  const [rate, setRate] = useState('6');
  const [timeType, setTimeType] = useState('years');
  const [period, setPeriod] = useState<number | null>(5);
  const [monthlyDeposit, setMonthlyDeposit] = useState<number | null>(null);
  const [totalInterest, setTotalInterest] = useState<number | null>(null);
  const [maturityAmount, setMaturityAmount] = useState<number | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [periodUnit, setPeriodUnit] = useState<'years' | 'months'>('years');
  const [totalInvestment, setTotalInvestment] = useState<number | null>(null);
  const [principalForChart, setPrincipalForChart] = useState(0);
  const [interestForChart, setInterestForChart] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [icon, setIcon] = useState<React.ReactNode>(<FavoriteBorderIcon />);
  const currencySymbol = useSelector(
    (state: RootState) => state.country.currencySymbol
  );
  const dispatch = useDispatch();
  const [, setSymbol] = useState(currencySymbol);
  const [savedTitle, setSavedTitle] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
 const [snackbarMessage, setSnackbarMessage] = useState("");

  const MAX_PERIOD = 10;


  const handleSubmit = async () => {
    if (!validateInputs()) {
      return;
    }
    const recurringDeposit: RecurringDeposit = {
      depositAmount: parseFloat(depositAmount.replace(/,/g, "")),
      rate: parseFloat(rate),
      period: parseInt(period?.toString() || '0', 10),
      currencySymbol,
      title: title,
      timeType: periodUnit
    };

    try {
      if (recurringData?.id) {
        await updateRecurringDeposit(recurringData.id, recurringDeposit);
        console.log(`Recurring deposit with ID ${recurringData.id} updated successfully.`);
      } else {
        await saveRecurringDeposit(recurringDeposit);
        console.log("New recurring deposit created successfully.");
      }
      setSnackbarMessage("Saved successfully!");
      setSnackbarOpen(true);
      setIcon(<FavoriteIcon style={{ color: "red" }} />);
      handleDialogClose();
      setTitle("");
    } catch (error) {
      console.error("Error saving recurring deposit:", error);
    }
  };


  useEffect(() => {
    const { recurring: passedRecurring, currencySymbol: passedCurrencySymbol } = location.state || {};
    console.log('Received location state:', location.state);

    if (passedCurrencySymbol) {
      dispatch(setCurrencySymbol(passedCurrencySymbol));
      setSymbol(passedCurrencySymbol);
    }

    if (passedRecurring) {
      setRecurringDeposit(passedRecurring);
      setIcon(<FavoriteIcon style={{ color: "red" }} />);
      setDepositAmount(formatNumberWithCommas(passedRecurring.depositAmount));
      setRate(passedRecurring.rate.toString());
      setPeriod(passedRecurring.period.toString());
      setPeriodUnit(passedRecurring.timeType);
      setTitle(passedRecurring.title);
      setSavedTitle(passedRecurring.title || "");
      handleCalculate()
    }
    handleCalculate()
  }, [location.state, recurringData]);


  const formatNumberWithCommas = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-IN').format(numValue);
  };
  const [showPdfButton, setShowPdfButton] = useState(false);
  const [isCalculated, setIsCalculated] = useState(false);


  const handleReset = () => {
    ReactGA.event({
      category: 'Recurring Deposit calculator',
      action: 'Recurring Deposit Reset Button',
      label: 'Recurring Deposit Calculator',
    });

    setDepositAmount('5000');
    setRate('6');
    setTimeType('');
    setPeriod(5);
    setTotalInterest(null);
    setMaturityAmount(null);
    setErrors({});
    setShowPdfButton(false);
  };



  const validateInputs = () => {
    const newErrors = {
      depositAmount: "",
      rate: "",
      period: "",
    };
    let isValid = true;

    const mindepositAmount = 100;

    if (!depositAmount) {
      newErrors.depositAmount = " Monthly Deposit amount is mandatory.";
      isValid = false;
    } else if (parseFloat(depositAmount.replace(/,/g, "")) < mindepositAmount) {
      newErrors.depositAmount = "Deposit amount can't be less than " + currencySymbol + "100.";
      isValid = false;
    }

    const rateValue = parseFloat(rate.replace(/,/g, ""));
    if (!rate) {
      newErrors.rate = "Interest rate is mandatory.";
      isValid = false;
    } else if (isNaN(rateValue) || rateValue < 1 || rateValue > 15) {
      newErrors.rate = 'Interest rate must be between 1 and 15.';
      isValid = false;
    }

    if (!period) {
      newErrors.period = "Period unit (years or months) is mandatory.";
      isValid = false;
    } else {
      const numericPeriod = parseFloat(period.toString().replace(/,/g, "")); // Convert to string here
      if (periodUnit === "years") {
        if (numericPeriod < 1 || numericPeriod > 10) {
          newErrors.period = "Years must be between 1 and 10.";
          isValid = false;
        }
      } else if (periodUnit === "months") {
        if (numericPeriod < 3 || numericPeriod > 120) {
          newErrors.period = "Months must be between 3 and 120.";
          isValid = false;
        }
      }
    }

    setErrors(newErrors);
    return isValid;
  };


  const handleCalculate = () => {
    ReactGA.event({ 'category': 'Recurring Deposit calculator', 'action': 'Recurring Deposit Calculate Button', 'label': 'Recurring Deposit Calculator' })
    if (!validateInputs()) {
      return;
    }

    const P = parseFloat(depositAmount.replace(/,/g, ''));
    const i = parseFloat(rate) / 400;
    let n = period || 0;

    if (periodUnit === 'years') {
      n = n * 4;
    } else if (periodUnit === 'months') {
      n = n / 3;
    }

    const totalInvestment = P * n * 3;
    setTotalInvestment(totalInvestment);


    const futureValue = P * ((Math.pow(1 + i, n) - 1) / (1 - Math.pow(1 + i, -1 / 3)));
    const totalReturns = futureValue - totalInvestment;

    setMaturityAmount(futureValue);
    setTotalInterest(totalReturns);
    setPrincipalForChart(totalInvestment);
    setInterestForChart(totalReturns);
    setShowPdfButton(true);
    setMonthlyDeposit(P);
    setIsCalculated(true)

  };

  const handleNumericChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    limit: number,
    decimalPlaces: number = 1
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/,/g, "");
  
    if (!/^\d*\.?\d*$/.test(rawValue)) return;
  
    const numericValue = parseFloat(rawValue);
    const parts = rawValue.split(".");
    if (parts.length > 2 || (parts[1] && parts[1].length > decimalPlaces)) return;

    if (rawValue === "0") return;
    if (rawValue.length > 0 && rawValue[0] === "0" && rawValue !== "0" && !rawValue.startsWith("0.")) return;
  
    if (rawValue === "" || (!isNaN(numericValue) && numericValue <= limit)) {
      setter(rawValue);
      if (errors[e.target.id]) {
        setErrors({ ...errors, [e.target.id]: "" });
      }
    } else {
      setErrors({
        ...errors,
        [e.target.id]: `Value must be between ${limit === 10000000 ? "₹100" : limit}`,
      });
    }
  };
  
  

  const calculateRD = () => {
    handleCalculate();
  };


  const handleBlur = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    value: string,
    id: string
  ) => {
    const numericValue = parseFloat(value.replace(/,/g, ''));
    if (!isNaN(numericValue)) {
      setter(formatNumberWithCommas(numericValue));
    }
  };

  const principalAmount = depositAmount && period ? parseFloat(depositAmount.replace(/,/g, '')) * (periodUnit === 'years' ? period * 12 : period) : 0;
  
  const total = principalForChart + interestForChart
  const percentagePrincipal  = Math.round((principalForChart/total)*100)
  const percentageInterest = Math.round((interestForChart/total)*100)
  const pieChartData = {
    labels: ['Principal (Total Deposits)%', 'Interest % '],
    datasets: [
      {
        data: [
          percentagePrincipal ,
          percentageInterest,
        ],
        backgroundColor: ['#36A2EB', '#FF6384'],
      },
    ],
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
    setTitle("");
  };

  const handleCancel = () => {
    handleDialogClose();
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      calculateRD();
    }
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason: "timeout" | "clickaway" | "escapeKeyDown"
  ) => {
    if (reason === 'clickaway') {
      return; 
    }
    setSnackbarOpen(false); 
  };
  return (
    <>
      <div className="container mt-4" onKeyPress={handleKeyPress}>

        <Helmet>
          <title>Recurring Deposit Calculator </title>
          <meta
            name="description"
            content="Easily calculate the maturity amount of your recurring deposit with our RD Calculator. Estimate your savings growth based on deposit amount, interest rate, and tenure."
          />
          <link rel="canonical" href="/recurring-deposit.html" />
        </Helmet>

        <SavedSnackbar
        open={snackbarOpen} 
        message={snackbarMessage} 
        onClose={handleSnackbarClose} 
      />
        <div className="container mt-5">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-2" style={{ fontFamily: "serif", fontSize: "30px", fontWeight: "bold" }}>
              Recurring Deposit Calculator
            </h5>
          </div>
          <p>Recurring Deposit (RD) is a financial savings scheme that allows individuals to deposit a fixed amount regularly for a specified period. It offers attractive interest rates,
            ensuring the growth of savings while instilling financial discipline.</p>
          <div className="row justify-content-center">
            {savedTitle && (
              <Typography variant="h6" style={{ marginLeft: '20px' }}>
                {savedTitle}
              </Typography>
            )}
            <div className="col-12 col-md-6 mb-4">
              <div className="card shadow-sm" style={{ width: '100%', height: '550px', margin: '0 auto', overflow: 'auto' }}>
                <div className="card-body">
                  <div className="mb-3">
                    <label htmlFor="depositAmount" className="form-label">
                      Monthly Deposit Amount ({currencySymbol})
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.depositAmount ? 'is-invalid' : ''}`}
                      id="depositAmount"
                      value={depositAmount}
                      onChange={handleNumericChange(setDepositAmount, 1000000)}
                      onBlur={() => handleBlur(setDepositAmount, depositAmount, 'depositAmount')}
                    />
                    {errors.depositAmount && <div className="invalid-feedback">{errors.depositAmount}</div>}
                  </div>
                  <div className="mb-3 d-flex justify-content-center">
                    <Slider
                      min={100}
                      max={1000000}
                      step={1000}
                      value={parseFloat(depositAmount.replace(/,/g, ''))}
                      onChange={(e, newValue) => setDepositAmount(newValue.toString())}
                      valueLabelDisplay="auto"
                      shiftStep={30}
                      sx={{ width: '95%' }}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="rate" className="form-label">
                      Annual Interest Rate (%)
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        className={`form-control ${errors.rate ? 'is-invalid' : ''}`}
                        id="rate"
                        value={rate}
                        onChange={handleNumericChange(setRate, 15, 2)}
                        onBlur={() => handleBlur(setRate, rate, 'rate')}
                      />
                      <span className="input-group-text">
                        <PercentIcon />
                      </span>
                      {errors.rate && <div className="invalid-feedback">{errors.rate}</div>}
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="period" className="form-label">Period</label>
                    <div className="input-group">
                      {errors.period && <div className="invalid-feedback">{errors.period}</div>}
                      <input
                      type="text"
                      className={`form-control ${
                        errors.period ? "is-invalid" : ""
                      }`}
                      id="period"
                      value={period !== null ? period : ""}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, "");
                        if (value === "") {
                          setPeriod(null);
                          setErrors((prev) => ({ ...prev, period: "" })); 
                        } else {
                          const numericValue = parseFloat(value);
                          if (
                            !isNaN(numericValue) &&
                            ((periodUnit === "years" &&
                              numericValue >= 1 &&
                              numericValue <= 10) ||
                              (periodUnit === "months" &&
                                numericValue >= 1 &&
                                numericValue <= 120))
                          ) {
                            setPeriod(numericValue);
                            setErrors((prev) => ({ ...prev, period: "" })); 
                          } else {
                            if (periodUnit === "years") {
                              if (numericValue > 10) {
                                setErrors((prev) => ({
                                  ...prev,
                                  period: "Maximum period is 10 years.",
                                }));
                              }
                            } else if (periodUnit === "months") {
                              if (numericValue > 120) {
                                setErrors((prev) => ({
                                  ...prev,
                                  period: "Maximum period is 120 months.",
                                }));
                              }
                            }
                          }
                        }
                      }}
                    />

                      <div className="input-group-append">
                        <div className="btn-group" role="group" aria-label="Period Unit">
                          {/* Years button */}
                          <input
                            type="radio"
                            className="btn-check"
                            name="periodUnit"
                            id="years"
                            value="years"
                            checked={periodUnit === 'years'}
                            onChange={() => {
                              if (periodUnit === 'months') {
                                //setPeriod((prevPeriod) => (parseInt(prevPeriod, 10) / 12).toString());
                                setPeriod((prevPeriod) => (prevPeriod || 0 / 12));

                              }
                              setPeriodUnit('years');
                            }}
                          />
                          <label className="btn btn-outline-primary rounded-0" htmlFor="years">Years</label>

                          {/* Months button */}
                          <input
                            type="radio"
                            className="btn-check"
                            name="periodUnit"
                            id="months"
                            value="months"
                            checked={periodUnit === 'months'}
                            onChange={() => {
                              if (periodUnit === 'years') {
                                //setPeriod((prevPeriod) => (parseInt(prevPeriod, 10) * 12).toString());
                                setPeriod((prevPeriod) => (prevPeriod || 0 * 12));

                              }
                              setPeriodUnit('months');
                            }}
                          />
                          <label className="btn btn-outline-primary" htmlFor="months">Months</label>
                        </div>
                      </div>
                      {errors.period && <div className="invalid-feedback">{errors.period}</div>}

                    </div>


                  </div>
                  <div className="d-flex justify-content-center " style={{ marginTop: '60px' }}>
                    <button
                      className="btn btn-primary btn-lg "
                      onClick={handleCalculate}

                    >
                      Calculate
                    </button>
                  </div>
                  <div className='d-flex justify-content-center'>
                    <a
                      onClick={handleReset}
                      style={{ cursor: "pointer", textDecoration: "none" }}
                      className=" pt-2"
                    >
                      Reset
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {maturityAmount !== null && totalInterest !== null && (
              <div className="col-12 col-md-6">
                <div className="card shadow-sm">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-4">Result</h5>
                      <BootstrapTooltip title="Save Calculation" placement="top">
                        <IconButton color="secondary" onClick={handleDialogOpen}>
                          {icon}
                        </IconButton>
                      </BootstrapTooltip>
                    </div>
                    <p className="card-text d-flex justify-content-between">
                      <strong>Monthly Deposit:</strong>
                      <span className="text-end">
                        {currencySymbol}  {monthlyDeposit !== null ? `${formatNumberWithCommas(Math.ceil(monthlyDeposit).toFixed(2))}` : ''}
                      </span>
                    </p>
                    <p className="card-text d-flex justify-content-between">
                      <strong>Total Interest:</strong>
                      <span className="text-end">
                        {currencySymbol}  {totalInterest !== null ? `${formatNumberWithCommas(Math.ceil(totalInterest).toFixed(2))}` : ''}
                      </span>
                    </p>
                    <p className="card-text d-flex justify-content-between">
                      <strong>Maturity Amount:</strong>
                      <span className="text-end">
                        {currencySymbol} {maturityAmount !== null ? `${formatNumberWithCommas(Math.ceil(maturityAmount).toFixed(2))}` : ''}
                      </span>
                    </p>
                  </div>
                </div>

                {totalInterest !== null && (
                  <div className="card shadow-sm mt-4 mb-3">
                    <div className="card-body">
                      <h5 className="card-title mb-4">Interest and Principal Breakdown</h5>
                      <div style={{ width: '300px', height: '260px', margin: '0 auto' }}>

                        <Pie data={pieChartData} />
                      </div>
                    </div>
                  </div>
                )}
                <div className="d-flex justify-content-end" style={{ marginTop: "75px", paddingLeft: "90px" }}>
                  {showPdfButton && (
                    <RecurringDepositPDFGenerator
                      depositAmount={parseFloat(depositAmount.replace(/,/g, ''))}
                      rate={parseFloat(rate)}
                      period={period || 0}
                      maturityAmount={maturityAmount}
                      totalInterest={totalInterest}
                      periodUnit={periodUnit}
                      isCalculated={isCalculated}

                    />
                  )}
                </div>
              </div>
            )}
          </div>
          <SaveTitleDialog
            open={dialogOpen}
            onClose={handleDialogClose}
            title={title}
            setTitle={setTitle}
            onSubmit={handleSubmit}
            onCancel={handleCancel}


          />
        </div>
      </div>

    </>
  );
};

export default RecurringDepositCalculator;